import { geoArea, geoBounds, geoCentroid } from 'd3-geo';
import { feature } from 'topojson-client';

export const MAX_COUNTRY_SCOPE = 10;
export const MIN_USER_ZOOM = 0.1;
export const MAX_USER_ZOOM = 12;
export const MAX_PROJECTION_SCALE = 6400;
export const MIN_PROJECTION_SCALE = 90;
export const ZOOM_BUTTON_STEP = 1.0;
export const ZOOM_SLIDER_STEP = 0.2;

const DEG_TO_RAD = Math.PI / 180;

export function createCountryAnyId(iso: string): number {
  const normalizedIso = String(iso || '').toUpperCase();
  const hash = normalizedIso.split('').reduce((sum, char, index) => sum + char.charCodeAt(0) * (index + 1), 0);
  return -100000 - hash;
}

export function isCountryAnyPost(post: any): boolean {
  return Boolean(post?.isCountryAny);
}

export function normalizeCountryName(name: string): string {
  const normalized = (name || '').trim().toLowerCase();
  if (normalized === 'united states') return 'united states of america';
  if (normalized === 'usa') return 'united states of america';
  if (normalized === 'uk') return 'united kingdom';
  return normalized;
}

function getLargestGeometry(geometry: any): any {
  if (!geometry) return null;

  if (geometry.type === 'Polygon') {
    return geometry;
  }

  if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0) {
    return geometry.coordinates
      .map((polygonCoordinates: any) => ({ type: 'Polygon', coordinates: polygonCoordinates }))
      .reduce((largest: any, current: any) => {
        if (!largest) return current;
        return geoArea({ type: 'Feature', properties: {}, geometry: current } as any) > geoArea({ type: 'Feature', properties: {}, geometry: largest } as any)
          ? current
          : largest;
      }, null);
  }

  if (geometry.type === 'GeometryCollection' && Array.isArray(geometry.geometries)) {
    return geometry.geometries
      .map((child: any) => getLargestGeometry(child))
      .filter((candidate: any) => Boolean(candidate))
      .reduce((largest: any, current: any) => {
        if (!largest) return current;
        return geoArea({ type: 'Feature', properties: {}, geometry: current } as any) > geoArea({ type: 'Feature', properties: {}, geometry: largest } as any)
          ? current
          : largest;
      }, null);
  }

  return null;
}

function getCountryRepresentativeCenter(countryFeature: any): [number, number] | null {
  if (!countryFeature) return null;

  const largestGeometry = getLargestGeometry(countryFeature.geometry);
  if (largestGeometry) {
    const [lon, lat] = geoCentroid({ type: 'Feature', properties: {}, geometry: largestGeometry } as any) as [number, number];
    if (Number.isFinite(lon) && Number.isFinite(lat)) {
      return [lon, lat];
    }
  }

  const [lon, lat] = geoCentroid(countryFeature) as [number, number];
  if (Number.isFinite(lon) && Number.isFinite(lat)) {
    return [lon, lat];
  }

  const [[minLon, minLat], [maxLon, maxLat]] = geoBounds(countryFeature) as [[number, number], [number, number]];
  return [(minLon + maxLon) / 2, (minLat + maxLat) / 2];
}

export function getSessionDefaultScope(options: string[], existingScope: string[], maxCountryScope: number = MAX_COUNTRY_SCOPE): string[] {
  const desiredCount = Math.min(2, maxCountryScope, options.length);
  if (desiredCount === 0) {
    return [];
  }

  const validExisting = existingScope.filter((iso) => options.includes(iso));
  const uniqueExisting = [...new Set(validExisting)];

  if (uniqueExisting.length < desiredCount) {
    const remaining = options.filter((iso) => !uniqueExisting.includes(iso));
    const shuffledRemaining = [...remaining].sort(() => Math.random() - 0.5);
    return [...uniqueExisting, ...shuffledRemaining.slice(0, desiredCount - uniqueExisting.length)];
  }

  return uniqueExisting.slice(0, desiredCount);
}

export function getCountryCentersByIso(
  geoTopology: any,
  availableCountryOptions: string[],
  countryNamesByIso: Record<string, string>,
): Record<string, [number, number]> {
  if (!geoTopology?.objects?.countries) return {} as Record<string, [number, number]>;

  try {
    const countriesFeatureCollection = feature(geoTopology, geoTopology.objects.countries) as any;
    const countries = countriesFeatureCollection?.features || [];

    const centersByNormalizedCountryName = new Map<string, [number, number]>();
    countries.forEach((country: any) => {
      const countryName = normalizeCountryName(String(country?.properties?.name || ''));
      if (!countryName) return;
      const center = getCountryRepresentativeCenter(country);
      if (!center) return;
      centersByNormalizedCountryName.set(countryName, center);
    });

    const result: Record<string, [number, number]> = {};
    availableCountryOptions.forEach((iso) => {
      const normalizedName = normalizeCountryName(countryNamesByIso[iso] || '');
      const center = centersByNormalizedCountryName.get(normalizedName);
      if (center) {
        result[iso] = center;
      }
    });

    return result;
  } catch {
    return {} as Record<string, [number, number]>;
  }
}

export function getCountryAnyPosts(
  selectedCountryIsos: string[],
  countryCentersByIso: Record<string, [number, number]>,
  countryNamesByIso: Record<string, string>,
): any[] {
  return selectedCountryIsos
    .map((iso) => {
      const center = countryCentersByIso[iso];
      if (!center) return null;

      return {
        id: createCountryAnyId(iso),
        iso,
        place: `${countryNamesByIso[iso] || iso} - Any`,
        activity: '',
        images: [],
        coordinates: '',
        isCountryAny: true,
        pinCoordinates: center,
      };
    })
    .filter((post): post is any => Boolean(post));
}

export function getSelectedCountriesBounds(
  geoTopology: any,
  normalizedSelectedCountryNames: Set<string>,
): [[number, number], [number, number]] | null {
  if (normalizedSelectedCountryNames.size === 0 || !geoTopology?.objects?.countries) return null;

  try {
    const countriesFeatureCollection = feature(geoTopology, geoTopology.objects.countries) as any;
    const countries = countriesFeatureCollection?.features || [];
    const matchedCountries = countries.filter((country: any) => normalizedSelectedCountryNames.has(normalizeCountryName(String(country?.properties?.name || ''))));
    if (matchedCountries.length === 0) return null;

    const [firstMin, firstMax] = geoBounds(matchedCountries[0]) as [[number, number], [number, number]];
    let minLon = firstMin[0];
    let minLat = firstMin[1];
    let maxLon = firstMax[0];
    let maxLat = firstMax[1];

    matchedCountries.slice(1).forEach((country: any) => {
      const [[countryMinLon, countryMinLat], [countryMaxLon, countryMaxLat]] = geoBounds(country) as [[number, number], [number, number]];
      minLon = Math.min(minLon, countryMinLon);
      minLat = Math.min(minLat, countryMinLat);
      maxLon = Math.max(maxLon, countryMaxLon);
      maxLat = Math.max(maxLat, countryMaxLat);
    });

    return [
      [minLon, minLat],
      [maxLon, maxLat],
    ];
  } catch {
    return null;
  }
}

export function createSafeFileTimestamp(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const mi = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return `${yyyy}${mm}${dd}-${hh}${mi}${ss}`;
}

export function resolveCssVariables(svgMarkup: string, sourceElement?: Element): string {
  const rootStyles = getComputedStyle(document.documentElement);
  const bodyStyles = getComputedStyle(document.body);
  const sourceStyles = sourceElement ? getComputedStyle(sourceElement) : null;

  return svgMarkup.replace(/var\(\s*(--[^,)\s]+)\s*(?:,\s*([^)]+))?\s*\)/g, (_fullMatch, cssVarName, fallbackValue) => {
    const resolved =
      sourceStyles?.getPropertyValue(cssVarName).trim() ||
      bodyStyles.getPropertyValue(cssVarName).trim() ||
      rootStyles.getPropertyValue(cssVarName).trim();

    if (resolved) return resolved;

    const fallback = String(fallbackValue || '').trim();
    return fallback || 'transparent';
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

interface ProjectionInput {
  mapViewport: { width: number; height: number };
  userZoom: number;
  selectedCountryIsos: string[];
  pinnedPinPosts: any[];
  visiblePinPosts: any[];
  selectedCountriesBounds: [[number, number], [number, number]] | null;
  maxProjectionScale?: number;
}

function normalizeLongitude(lon: number): number {
  const wrapped = ((lon + 180) % 360 + 360) % 360 - 180;
  return wrapped === -180 ? 180 : wrapped;
}

export function getRouteLineSegments(
  from: [number, number],
  to: [number, number],
): Array<{ from: [number, number]; to: [number, number] }> {
  const fromLon = normalizeLongitude(Number(from?.[0]));
  const fromLat = Number(from?.[1]);
  const toLon = normalizeLongitude(Number(to?.[0]));
  const toLat = Number(to?.[1]);

  if (!Number.isFinite(fromLon) || !Number.isFinite(fromLat) || !Number.isFinite(toLon) || !Number.isFinite(toLat)) {
    return [];
  }

  const directDelta = Math.abs(toLon - fromLon);
  if (directDelta <= 180) {
    return [{ from: [fromLon, fromLat], to: [toLon, toLat] }];
  }

  const midpointLon = normalizeLongitude((fromLon + toLon) / 2);
  const midpointLat = (fromLat + toLat) / 2;

  return [
    { from: [fromLon, fromLat], to: [midpointLon, midpointLat] },
    { from: [midpointLon, midpointLat], to: [toLon, toLat] },
  ];
}

function getLongitudeSpanAndCenter(longitudes: number[]): { span: number; center: number } {
  if (longitudes.length === 0) return { span: 360, center: 0 };
  if (longitudes.length === 1) return { span: 0, center: normalizeLongitude(longitudes[0]) };

  const sorted = longitudes.map(normalizeLongitude).sort((a, b) => a - b);
  let largestGap = -1;
  let largestGapStartIndex = 0;

  for (let i = 0; i < sorted.length; i += 1) {
    const current = sorted[i];
    const next = i === sorted.length - 1 ? sorted[0] + 360 : sorted[i + 1];
    const gap = next - current;
    if (gap > largestGap) {
      largestGap = gap;
      largestGapStartIndex = i;
    }
  }

  const arcStart = sorted[(largestGapStartIndex + 1) % sorted.length];
  let arcEnd = sorted[largestGapStartIndex];
  if (arcEnd < arcStart) arcEnd += 360;

  const span = Math.max(0, arcEnd - arcStart);
  const center = normalizeLongitude(arcStart + span / 2);
  return { span, center };
}

function getDirectLongitudeSpanAndCenter(longitudes: number[]): { span: number; center: number } {
  if (longitudes.length === 0) return { span: 360, center: 0 };
  if (longitudes.length === 1) {
    const lon = normalizeLongitude(longitudes[0]);
    return { span: 0, center: lon };
  }

  const normalized = longitudes.map(normalizeLongitude);
  const minLon = Math.min(...normalized);
  const maxLon = Math.max(...normalized);
  return {
    span: Math.max(0, maxLon - minLon),
    center: normalizeLongitude((minLon + maxLon) / 2),
  };
}

function getFitScale({
  viewportWidth,
  viewportHeight,
  lonSpan,
  latSpan,
  fitPadding,
  maxProjectionScale,
}: {
  viewportWidth: number;
  viewportHeight: number;
  lonSpan: number;
  latSpan: number;
  fitPadding: number;
  maxProjectionScale: number;
}): number {
  const wideLongitudePadding =
    lonSpan >= 175
      ? 1.48
      : lonSpan >= 165
        ? 1.34
        : lonSpan >= 150
          ? 1.22
          : lonSpan >= 130
            ? 1.12
            : 1;
  const paddedLonSpan = Math.max(1, lonSpan * fitPadding * wideLongitudePadding);
  const paddedLatSpan = Math.max(1, latSpan * fitPadding);
  const widthScale = (viewportWidth * 0.94) / (paddedLonSpan * DEG_TO_RAD);
  const heightScale = (viewportHeight * 0.92) / (paddedLatSpan * DEG_TO_RAD);
  const rawScale = Math.min(widthScale, heightScale);

  return Math.max(MIN_PROJECTION_SCALE, Math.min(maxProjectionScale, rawScale));
}

export function getMapProjectionConfig({
  mapViewport,
  userZoom,
  selectedCountryIsos,
  pinnedPinPosts,
  visiblePinPosts,
  selectedCountriesBounds,
  maxProjectionScale = MAX_PROJECTION_SCALE,
}: ProjectionInput): { scale: number; center: [number, number] } {
  const viewportWidth = mapViewport.width || 900;
  const viewportHeight = mapViewport.height || 450;
  const widthRatio = viewportWidth / 900;
  const heightRatio = viewportHeight / 450;
  const minRatio = Math.min(widthRatio, heightRatio);
  const viewportAspect = viewportWidth / Math.max(1, viewportHeight);
  const aspectPenalty = viewportAspect < 1.65 ? Math.max(0.84, viewportAspect / 1.65) : viewportAspect > 2.45 ? Math.max(0.88, 2.45 / viewportAspect) : 1;
  const smoothBaseScale = Math.min(1, Math.pow(minRatio, 0.75));
  const viewportScaleFactor = Math.max(0.58, Math.min(1, smoothBaseScale * aspectPenalty));

  if (selectedCountryIsos.length === 0) {
    return { scale: 165 * viewportScaleFactor * userZoom, center: [0, 0] };
  }

  const fitPosts = pinnedPinPosts.length > 0 ? pinnedPinPosts : visiblePinPosts;

  if (fitPosts.length > 0) {
    const longitudes = fitPosts.map((post) => Number(post?.pinCoordinates?.[0])).filter((value) => Number.isFinite(value));
    const latitudes = fitPosts.map((post) => Number(post?.pinCoordinates?.[1])).filter((value) => Number.isFinite(value));

    if (longitudes.length > 0 && latitudes.length > 0) {
      const minLat = Math.min(...latitudes);
      const maxLat = Math.max(...latitudes);

      const { span: rawLonSpan, center: centerLon } = getDirectLongitudeSpanAndCenter(longitudes);
      const centerLat = (minLat + maxLat) / 2;
      const lonSpan = Math.max(1.2, rawLonSpan);
      const latSpan = Math.max(0.9, maxLat - minLat);
      const computedScale = getFitScale({
        viewportWidth,
        viewportHeight,
        lonSpan,
        latSpan,
        fitPadding: 1.16,
        maxProjectionScale,
      });

      return {
        scale: computedScale * userZoom,
        center: [centerLon, centerLat],
      };
    }
  }

  if (selectedCountriesBounds) {
    const [[minLon, minLat], [maxLon, maxLat]] = selectedCountriesBounds;
    const { span: rawLonSpan, center: centerLon } = getLongitudeSpanAndCenter([minLon, maxLon]);
    const centerLat = (minLat + maxLat) / 2;
    const lonSpan = Math.max(1.5, rawLonSpan);
    const latSpan = Math.max(1.2, maxLat - minLat);
    const computedScale = getFitScale({
      viewportWidth,
      viewportHeight,
      lonSpan,
      latSpan,
      fitPadding: 1.2,
      maxProjectionScale,
    });

    return {
      scale: computedScale * userZoom,
      center: [centerLon, centerLat],
    };
  }

  return { scale: 165 * viewportScaleFactor * userZoom, center: [0, 0] };
}
